import { CancelToken } from 'axios';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../Common/api/ABHub/ABHubGlobalConfig';
import { agpAxiosClient } from '../../Common/api/ABHub/ABHubTokenConfig';
import { RequestResult } from '../../AGCP/models/AirGapMessage';

/**
 * Result entry returned by the SRF `POST /api/storage/check-hashes` endpoint.
 * One row per (hash, container) match, plus one row per requested hash that was
 * not found in any container (with `exists = false` and the location/timestamp
 * fields left null).
 */
export type StorageBlobInfo = {
    hash?: string;
    exists: boolean;
    containerName?: string;
    blobPath?: string;
    storageAccountUri?: string;
    /** UTC timestamp when the blob was created (uploaded). */
    createdOn?: string;
    /** UTC timestamp when the blob will expire (createdOn + 30-day lifecycle). */
    expiresOn?: string;
    /**
     * Whether the hash was found in the hydration cache (e.g. `dehydrationfiles`).
     * - `true` / `false` — cache was probed and the blob was found / not found.
     * - `null` / `undefined` — the environment has no cache account configured,
     *   so the field is not applicable. Only dev / prodmock / prod populate this.
     */
    existsInCache?: boolean | null;
};

/**
 * Calls the SRF `CheckHashInStorage` admin endpoint to verify which of the supplied
 * hashes exist as `Builds/{hash}` blobs in the configured transfer containers.
 *
 * @param hashes Hashes to look up (typically build hashes / file hashes).
 * @param selectedCloud Optional cloud filter:
 *   - `undefined` / empty: use the cloud-configured NormalLane + PriorityLane (default).
 *   - `"all"`: probe every container whose name contains "transfer" in the storage account.
 *   - cloud moniker (e.g. `"ussec"`, `"usnat"`, `"bleu"`): probe `{cloud}-transfer-normal`
 *     and `{cloud}-transfer-priority`.
 */
export const checkHashInStorage = async (
    hashes: string[],
    selectedCloud?: string,
    cancelToken?: CancelToken
): Promise<RequestResult<StorageBlobInfo[]>> => {
    const srfUrl = getConfig(GlobalConfigProperties.SrfUrl);
    if (!srfUrl) {
        throw new Error(
            'SRF (ABSM) URL is not configured. Add an "SRF": { "Url": "..." } ' +
                'section to the AB Hub appsettings for this environment.'
        );
    }

    const response = await agpAxiosClient.post<
        RequestResult<StorageBlobInfo[]>
    >(
        `${srfUrl}/api/storage/check-hashes`,
        {
            hashes,
            selectedCloud: selectedCloud ?? null,
        },
        { cancelToken }
    );

    return response.data;
};

/**
 * Direction of a blob transfer between the hydration cache account and a cloud
 * transfer container. Matches the SRF `TransferDirection` enum by ordinal value.
 * - `CacheToContainer` (0): copy from the cache (keyed by raw hash) into the transfer
 *   container (keyed under `Builds/{hash}`).
 * - `ContainerToCache` (1): copy from the transfer container into the cache.
 */
export enum TransferDirection {
    CacheToContainer = 0,
    ContainerToCache = 1,
}

/**
 * Work lane that selects which transfer container is used. Matches the SRF
 * `WorkLane` enum (Warm = 0 / normal, Dcmt = 1 / priority).
 */
export enum WorkLane {
    Warm = 0,
    Dcmt = 1,
}

/**
 * Result entry returned by the SRF `POST /api/storage/transfer-blobs` endpoint.
 * One row per requested hash.
 */
export type BlobTransferResult = {
    hash?: string;
    /** Whether the transfer succeeded (also true when skipped because it already existed). */
    transferred: boolean;
    /** Whether the copy was skipped because the destination blob already existed. */
    skippedExisting: boolean;
    /** Full source blob path (raw hash on the cache side, `Builds/{hash}` on the transfer side). */
    sourceBlobPath?: string;
    /** Full destination blob path the source was copied to. */
    destinationBlobPath?: string;
    /** Destination container the blob was written to. */
    destinationContainerName?: string;
    /** Destination storage account base URI the blob was written to. */
    destinationAccountUri?: string;
    /** Human-readable outcome (failure reason, "source not found", "skipped", etc.). */
    message?: string;
};

/**
 * Calls the SRF `TransferBlobs` admin endpoint to copy build blobs between the
 * hydration cache account and a cloud transfer container, in either direction.
 * Copies are performed server-side (the destination account pulls the source via a
 * short-lived MSI-minted read-only SAS), so no blob content flows through the portal.
 *
 * @param hashes Hashes to transfer.
 * @param direction Transfer direction (defaults to `CacheToContainer`).
 * @param workLane Work lane selecting the transfer container (defaults to `Warm`).
 * @param overwrite Whether to overwrite an existing destination blob (defaults to `false`).
 */
export const transferBlobs = async (
    hashes: string[],
    direction: TransferDirection = TransferDirection.CacheToContainer,
    workLane: WorkLane = WorkLane.Warm,
    overwrite = false,
    cancelToken?: CancelToken
): Promise<RequestResult<BlobTransferResult[]>> => {
    const srfUrl = getConfig(GlobalConfigProperties.SrfUrl);
    if (!srfUrl) {
        throw new Error(
            'SRF (ABSM) URL is not configured. Add an "SRF": { "Url": "..." } ' +
                'section to the AB Hub appsettings for this environment.'
        );
    }

    const response = await agpAxiosClient.post<
        RequestResult<BlobTransferResult[]>
    >(
        `${srfUrl}/api/storage/transfer-blobs`,
        {
            hashes,
            direction,
            workLane,
            overwrite,
        },
        { cancelToken }
    );

    return response.data;
};
